import {
    CapacitorBarcodeScanner,
    CapacitorBarcodeScannerTypeHint
} from '@capacitor/barcode-scanner';
import { Capacitor } from '@capacitor/core';

const SCAN_CANCELLED_ERROR_CODE = 'OS-PLUG-BARC-0006';
const IOS_PLATFORM = 'ios';

// iOS: scanBarcode resolves while the scanner screen is still being dismissed; UIKit then
// drops a screen presented right away (the SSO in-app browser loads but never shows).
// ponytail: fixed delay — if it proves short on slow devices, wait for the dismissal natively.
const IOS_SCANNER_DISMISS_DELAY_MS = 600;

const waitForIosScannerDismissal = (): Promise<void> =>
    Capacitor.getPlatform() === IOS_PLATFORM
        ? new Promise((resolve) => {
              setTimeout(resolve, IOS_SCANNER_DISMISS_DELAY_MS);
          })
        : Promise.resolve();

function isScanCancelledError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    const code = (error as Error & { code?: string }).code;
    if (code === SCAN_CANCELLED_ERROR_CODE) return true;
    return /cancel/i.test(error.message);
}

/**
 * Launches the native QR scanner (ready-to-use UI) and returns the raw scanned
 * string, or null (cancelled / nothing scanned). Domain-agnostic.
 *
 * Both platforms use OSBarcodeLib (AVFoundation/Vision on iOS, CameraX on
 * Android) — camera permission required (Info.plist / AndroidManifest). Other
 * errors (e.g. camera access denied) propagate to the caller. Web is not handled.
 */
export async function scanQr(): Promise<string | null> {
    try {
        const { ScanResult } = await CapacitorBarcodeScanner.scanBarcode({
            hint: CapacitorBarcodeScannerTypeHint.QR_CODE
        });
        if (!ScanResult) return null;
        await waitForIosScannerDismissal();
        return ScanResult;
    } catch (error) {
        if (isScanCancelledError(error)) return null;
        throw error;
    }
}
